import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ReplayIcon from '@material-ui/icons/Replay';
import { useTracking } from 'react-tracking';
import { CountryInteractiveFilter } from './components/countryInteractiveFilter';
import { CityInteractiveFilter } from './components/cityInteractiveFilter';
import { ComposerInteractiveFilter } from './components/composerInteractiveFilter';
import { CompanyInteractiveFilter } from './components/companyInteractiveFilter';
import { WorkInteractiveFilter } from './components/workInteractiveFilter';
import { ViewByFilter } from './components/viewByFilter';
import { DateRangeFilter } from './components/dateRangeFilter';
import TextButton from '../../components/TextButton';
import Button from '../../components/Button';
import { interactiveFilterPanelStyle } from './style';
import { filterReducer } from './reducer';
import { TP } from '../../constants/consts';
import { useTranslation } from '../../src/i18n';
import { BottomSheet } from '../../components/BottomSheet';
import { getOptions } from './aggregationUtil';
import { futureDateUpTo1Years, getValidDateRange, pastDateUpTo3Years } from '../../utils/common';
import { trackSearch, trackClearForm, trackSearchGo } from '../../utils/tracking';
import { createDate } from '../../utils/date';
import 'promise.allsettled';

const useStyles = makeStyles(() => interactiveFilterPanelStyle());
const formatDate = dateToFormat => {
  if (dateToFormat instanceof Date) {
    return createDate(dateToFormat).format('YYYY-MM-DD');
  }
  return '';
};
function isEmpty(str) {
  return !str || str.length === 0;
}
const addDays = (dt, days) => {
  const result = new Date(dt);
  result.setDate(result.getDate() + days);
  return result;
};
const getValidToDate = (date_from, date_to) => {
  if (!isEmpty(date_to)) {
    return date_to;
  }
  if (!isEmpty(date_from)) {
    return date_from;
  }
  return new Date();
};
const getValidFromDate = (date_from, date_to) => {
  if (!isEmpty(date_from)) {
    return date_from;
  }
  if (!isEmpty(date_to)) {
    return date_to;
  }
  return new Date();
};
const NO_DATE = '';
const defaultState = {
  endpoint: 'productions/aggregations',
  query: {
    aggregation_query: '',
  },
  date_from: new Date(),
  date_to: NO_DATE,
  tickets: false,
  live_stream: false,
  selectedValue: {
    countries: null,
    cities: null,
    composers: null,
    works: null,
    companies: null,
  },
};
const filterDispatchTypes = Object.freeze({
  country_id: {
    select: 'SELECT_COUNTRY',
    clear: 'CLEAR_COUNTRY',
    searchByClear: 'SEARCH_BY_REMOVE_CHARACTERS_COUNTRY',
    collection: 'countries',
  },
  city_id: {
    select: 'SELECT_CITY',
    clear: 'CLEAR_CITY',
    searchByClear: 'SEARCH_BY_REMOVE_CHARACTERS_CITY',
    collection: 'cities',
  },
  composer_id: {
    select: 'SELECT_COMPOSER',
    clear: 'CLEAR_COMPOSER',
    searchByClear: 'SEARCH_BY_REMOVE_CHARACTERS_COMPOSER',
    collection: 'composers',
  },
  company_id: {
    select: 'SELECT_COMPANY',
    clear: 'CLEAR_COMPANY',
    searchByClear: 'SEARCH_BY_REMOVE_CHARACTERS_COMPANY',
    collection: 'companies',
  },
  work_id: {
    select: 'SELECT_WORK',
    clear: 'CLEAR_WORK',
    searchByClear: 'SEARCH_BY_REMOVE_CHARACTERS_WORK',
    collection: 'works',
  },
});

/**
 *  Fluid Mode - Default Mode and all the elements arrange as per Flex box with no restrictions
 *               suitable for desktop
 *  Compact - This mode is for constrained layout e.g Homepage
 *  Mobile - This mode is combination of Compact mode with a slider and ability to hide/show on user action
 * @type {Readonly<{COMPACT: string, FLUID: string, MOBILE: string}>}
 */
export const filterViewMode = Object.freeze({
  FLUID: 'fluid',
  COMPACT: 'compact',
  MOBILE: 'mobile',
});

export const filters = Object.freeze({
  COUNTRY_ID: 'country_id',
  CITY_ID: 'city_id',
  COMPOSER_ID: 'composer_id',
  COMPANY_ID: 'company_id',
  WORK_ID: 'work_id',
});

const getFinalQueryConditions = state => {
  const { date_from, date_to, query, tickets } = state;
  const { dayFrom, dayTo } = getValidDateRange(date_from, date_to);
  // add live_stream here to make it work
  return { date_from: dayFrom, date_to: dayTo, ...query, tickets };
};

function fetchSelectedValues(selectedValues) {
  const validFilterQueries = Object.keys(filterDispatchTypes)
    .filter(filterType => typeof selectedValues[filterType] !== 'undefined')
    .map(filterType => ({
      aggregation_type: filterType,
      aggregation_query: '',
      [filterType]: selectedValues[filterType],
    }));
  const filterPromises = validFilterQueries.map(filterQuery => getOptions({ query: filterQuery }));
  const selectedFilterValues = Promise.allSettled(filterPromises).then(response =>
    validFilterQueries.map((validFilterQuery, index) => ({
      ...validFilterQuery,
      result: response[index],
    })),
  );
  return selectedFilterValues.then(validFiltersValues => {
    const formattedSelectedValue = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const key in validFiltersValues) {
      if (validFiltersValues[key].result.value?.data) {
        const filterKey = [filterDispatchTypes[validFiltersValues[key].aggregation_type].collection]; // countries/cities etc.
        // eslint-disable-next-line prefer-destructuring
        formattedSelectedValue[filterKey] = validFiltersValues[key].result.value?.data[0];
      }
    }
    // format should be like
    // selectedValue: {
    //   countries: {
    //     id: '57',
    //       key: '57',
    //       name: 'Germany',
    //       selected: false,
    //       slug: 'germany',
    //       total: 28736,
    //   },
    //   cities: null,
    // },
    return formattedSelectedValue;
  });
}

export function InteractiveFilterPanel({
  title,
  subTitle,
  countries,
  cities,
  composers,
  companies,
  musicalWorks,
  viewMode = filterViewMode.FLUID,
  onSubmit,
  onClear,
  selectedValues = {},
  performanceDateRange = {
    performanceStartDate: pastDateUpTo3Years,
    performanceEndDate: futureDateUpTo1Years,
  },
  ...additionalProps
}) {
  const classes = useStyles();
  const { className: externalClasses, ...restOfPropsAndHandlers } = additionalProps;
  const { t } = useTranslation();
  const [filterState, dispatch] = React.useReducer(filterReducer, defaultState);
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedValue, setSelectedValue] = React.useState(selectedValues);
  const isOneOfTheExistingFilters = filterType =>
    filterType === filters.COUNTRY_ID ||
    filterType === filters.CITY_ID ||
    filterType === filters.COMPANY_ID ||
    filterType === filters.COMPOSER_ID ||
    filterType === filters.WORK_ID;

  const i18NText = {
    pageTitle: title || t(`${TP}.PRODUCTIONS_AND_PERFORMANCES`),
    pageSubTitle: subTitle || t(`${TP}.PRODUCTIONS_AND_PERFORMANCES_SUBTITLE`, { performancesCount: 50000 }),
    viewByLabel: t(`${TP}.m_VIEWBY`),
    viewByAll: t(`${TP}.m_SEASON_ALL`),
    viewByTickets: t(`${TP}.FN_TICKETS`),
    viewByLiveStream: t(`${TP}.FN_LIVESTREAM`),
    countriesFilter: t(`${TP}.m_COUNTRY`),
    citiesFilter: t(`${TP}.m_CITY`),
    composersFilter: t(`${TP}.m_COMPOSER`),
    companiesFilter: t(`${TP}.m_COMPANY`),
    musicalWorksFilter: t(`${TP}.m_MUSICALWORK`),
    today: t(`${TP}.m_TODAY`),
    tomorrow: t(`${TP}.m_TOMORROW`),
    plus7days: t(`${TP}.X_DAYS`, { days: '+7' }),
    plus14days: t(`${TP}.X_DAYS`, { days: '+14' }),
    plus30days: t(`${TP}.X_DAYS`, { days: '+30' }),
    clearAll: t(`${TP}.FORM_CLEAR_ALL`),
    search: t(`${TP}.m_ARTIST_SEARCH_BUTTON`),
    from: t(`${TP}.m_FROM`),
    to: t(`${TP}.m_TO`),
  };
  const tracking = useTracking();

  React.useEffect(() => {
    const {
      date_from,
      date_to,
      tickets,
      live_stream,
      country_id,
      city_id,
      composer_id,
      company_id,
      work_id,
    } = selectedValues;

    const validFilters = fetchSelectedValues(selectedValues);
    // conditionally spread the operators
    const selectedFilterQuery = {
      ...(country_id && { country_id }),
      ...(city_id && { city_id }),
      ...(composer_id && { composer_id }),
      ...(company_id && { company_id }),
      ...(work_id && { work_id }),
    };
    validFilters.then(filterValues => {
      const formattedDates = {
        date_from: date_from ? new Date(date_from) : new Date(),
        date_to: date_to ? new Date(date_to) : NO_DATE,
      };
      dispatch({
        type: 'SET_SELECTED_VALUES',
        payload: {
          state: {
            ...formattedDates,
            ...{ selectedValue: filterValues },
            tickets: typeof tickets !== 'undefined' ? tickets === 'true' : defaultState.tickets,
            live_stream: typeof tickets !== 'undefined' ? live_stream === 'true' : defaultState.live_stream,
            query: { ...selectedFilterQuery },
          },
        },
      });
    });
  }, [selectedValue]);

  const onSelectValue = (filterType, value) => {
    if (isOneOfTheExistingFilters(filterType)) {
      dispatch({
        type: filterDispatchTypes[filterType].select,
        payload: {
          additionalFilters: { [filterType]: value.id },
          selectedValue: {
            [filterDispatchTypes[filterType].collection]: value,
          },
        },
      });
      tracking.trackEvent(trackSearch(filterType, value?.name));
    }
  };
  const onClearValue = filterType => {
    if (isOneOfTheExistingFilters(filterType)) {
      dispatch({
        type: filterDispatchTypes[filterType].clear,
        payload: {
          id: filterType,
          collection: filterDispatchTypes[filterType].collection,
        },
      });
    }
  };
  const onSearchByRemoveCharacters = filterType => {
    if (isOneOfTheExistingFilters(filterType)) {
      dispatch({
        type: filterDispatchTypes[filterType].searchByClear,
        payload: {
          id: filterType,
        },
      });
    }
  };

  const onSearch = (keyword, filterType) => {
    if (keyword && keyword.length === 0) {
      onClearValue(filterType);
    } else {
      onSearchByRemoveCharacters(filterType);
    }

    tracking.trackEvent(trackSearch(filterType, keyword));
  };
  const filterForm = (
    <div
      className={classnames({
        [classes.interactiveFilterPanel]: true,
        [classes.interactiveFilterPanel_compact]: viewMode === filterViewMode.COMPACT,
        [classes.interactiveFilterPanel_mobile]: viewMode === filterViewMode.MOBILE,
        [externalClasses || '']: true,
      })}
      {...restOfPropsAndHandlers}
    >
      <form className={classnames(classes.form)} autoComplete="off" onSubmit={e => e.preventDefault()}>
        <section className={classes.gridContainer}>
          <div className={classes.gridRow}>
            <div className={(classes.gridRowItem, classes.titleSection)}>
              <div className={classes.titleGroup}>
                <h1 className={classes.title}>{i18NText.pageTitle}</h1>
                <h5 className={classes.subtitle}>{i18NText.pageSubTitle}</h5>
              </div>
              <div className={classes.mobileSearch}>
                <IconButton
                  className={classes.mobileSearch_icon}
                  color="inherit"
                  aria-label="close"
                  onClick={() => {
                    setDrawerOpen(false);
                  }}
                >
                  <CloseIcon className={classnames(classes.closeIcon, classes.mobileSearch_icon_absolute)} />
                </IconButton>
              </div>
            </div>
          </div>
          <div className={classnames(classes.gridRow, classes.viewByRow)}>
            <ViewByFilter
              title={i18NText.viewByLabel}
              viewByAll={{
                isChecked: filterState.tickets && filterState.live_stream,
                label: i18NText.viewByAll,
                onChange: val => {
                  dispatch({
                    type: 'UPDATE_VIEW_BY',
                    payload: {
                      tickets: val,
                      live_stream: val,
                    },
                  });
                },
              }}
              viewByTickets={{
                isChecked: filterState.tickets,
                label: i18NText.viewByTickets,
                onChange: val => {
                  dispatch({
                    type: 'UPDATE_VIEW_BY',
                    payload: {
                      tickets: val,
                    },
                  });
                },
              }}
              viewByLiveStream={{
                isChecked: filterState.live_stream,
                label: i18NText.viewByLiveStream,
                onChange: val => {
                  dispatch({
                    type: 'UPDATE_VIEW_BY',
                    payload: {
                      live_stream: val,
                    },
                  });
                },
              }}
            />
          </div>
          <div className={classnames(classes.gridRow, classes.filterSection)}>
            <div key="filter-country" className={classnames(classes.gridRowItem, classes.dropDownFilter)}>
              <CountryInteractiveFilter
                countries={countries}
                aggregationQuery={{
                  ...filterState,
                  query: { aggregation_type: 'country_id', ...filterState.query },
                }}
                onSelectValue={onSelectValue}
                onClearValue={onClearValue}
                onSearch={keyword => {
                  onSearch(keyword, filters.COUNTRY_ID);
                }}
                label={i18NText.countriesFilter}
                value={filterState.selectedValue.countries}
                isMobile={viewMode === filterViewMode.MOBILE}
              />
            </div>
            <div key="filter-city" className={classnames(classes.gridRowItem, classes.dropDownFilter)}>
              <CityInteractiveFilter
                cities={cities}
                aggregationQuery={{
                  ...filterState,
                  query: { aggregation_type: 'city_id', ...filterState.query },
                }}
                onSelectValue={onSelectValue}
                onClearValue={onClearValue}
                onSearch={keyword => {
                  onSearch(keyword, filters.CITY_ID);
                }}
                label={i18NText.citiesFilter}
                value={filterState.selectedValue.cities}
                isMobile={viewMode === filterViewMode.MOBILE}
              />
            </div>
            <div key="filter-company" className={classnames(classes.gridRowItem, classes.dropDownFilter)}>
              <CompanyInteractiveFilter
                companies={companies}
                aggregationQuery={{
                  ...filterState,
                  query: { aggregation_type: 'company_id', ...filterState.query },
                }}
                onSelectValue={onSelectValue}
                onClearValue={onClearValue}
                onSearch={keyword => {
                  onSearch(keyword, filters.COMPANY_ID);
                }}
                label={i18NText.companiesFilter}
                value={filterState.selectedValue.companies}
                isMobile={viewMode === filterViewMode.MOBILE}
              />
            </div>
            <div key="filter-composer" className={classnames(classes.gridRowItem, classes.dropDownFilter)}>
              <ComposerInteractiveFilter
                composers={composers}
                aggregationQuery={{
                  ...filterState,
                  query: { aggregation_type: 'composer_id', ...filterState.query },
                }}
                onSelectValue={onSelectValue}
                onClearValue={onClearValue}
                onSearch={keyword => {
                  onSearch(keyword, filters.COMPOSER_ID);
                }}
                label={i18NText.composersFilter}
                value={filterState.selectedValue.composers}
                isMobile={viewMode === filterViewMode.MOBILE}
              />
            </div>

            <div
              key="filter-work"
              className={classnames(classes.gridRowItem, classes.musicalWorks, classes.dropDownFilter)}
            >
              <WorkInteractiveFilter
                musicalWorks={musicalWorks}
                aggregationQuery={{
                  ...filterState,
                  query: { aggregation_type: 'work_id', ...filterState.query },
                }}
                onSelectValue={onSelectValue}
                onClearValue={onClearValue}
                onSearch={keyword => {
                  onSearch(keyword, filters.WORK_ID);
                }}
                label={i18NText.musicalWorksFilter}
                value={filterState.selectedValue.works}
                isMobile={viewMode === filterViewMode.MOBILE}
              />
            </div>
            <DateRangeFilter
              performanceDateRange={performanceDateRange}
              dateFrom={{
                className: classnames({
                  [classes.gridRowItem]: true,
                  [classes.dateFilterMobile]: viewMode === filterViewMode.MOBILE,
                }),
                label: '',
                placeholder: i18NText.from,
                selectedDay: formatDate(filterState.date_from, 'from_filter'),
                disabledDays: [{ after: filterState.date_to }],
                onChange: day => {
                  dispatch({
                    type: 'UPDATE_DATE',
                    payload: {
                      new_date_from: day,
                    },
                  });
                  tracking.trackEvent(trackSearch('date_from', day));
                },
              }}
              dateTo={{
                label: '',
                placeholder: i18NText.to,
                className: classnames({
                  [classes.gridRowItem]: true,
                  [classes.dateFilterMobile]: viewMode === filterViewMode.MOBILE,
                }),
                selectedDay: formatDate(filterState.date_to, 'to_filter'),
                disabledDays: [{ before: filterState.date_from }],
                onChange: day => {
                  dispatch({
                    type: 'UPDATE_DATE',
                    payload: {
                      new_date_to: day,
                    },
                  });
                  tracking.trackEvent(trackSearch('date_to', day));
                },
              }}
              addDays={{
                group: {
                  className: classnames(classes.gridRowItem, classes.noMarginTop),
                },
                today: {
                  label: i18NText.today,
                  onChange: () => {
                    dispatch({
                      type: 'UPDATE_DATE',
                      payload: {
                        new_date_to: new Date(),
                        new_date_from: new Date(),
                      },
                    });
                  },
                },
                tomorrow: {
                  label: i18NText.tomorrow,
                  onChange: () => {
                    dispatch({
                      type: 'UPDATE_DATE',
                      payload: {
                        new_date_to: addDays(new Date(), 1),
                        new_date_from: addDays(new Date(), 1),
                      },
                    });
                  },
                },
                plus7: {
                  label: i18NText.plus7days,
                  onClick: () => {
                    const new_date_from = getValidFromDate(filterState.date_from, filterState.date_to);
                    const new_date_to = addDays(getValidToDate(filterState.date_to, filterState.date_to), 7);
                    dispatch({
                      type: 'UPDATE_DATE',
                      payload: {
                        new_date_to,
                        new_date_from,
                      },
                    });
                  },
                },
                plus14: {
                  label: i18NText.plus14days,
                  onClick: () => {
                    const new_date_from = getValidFromDate(filterState.date_from, filterState.date_to);
                    const new_date_to = addDays(getValidToDate(filterState.date_from, filterState.date_to), 14);

                    dispatch({
                      type: 'UPDATE_DATE',
                      payload: {
                        new_date_to,
                        new_date_from,
                      },
                    });
                  },
                },
                plus30: {
                  label: i18NText.plus30days,
                  onClick: () => {
                    const new_date_from = getValidFromDate(filterState.date_from, filterState.date_to);
                    const new_date_to = addDays(getValidToDate(filterState.date_from, filterState.date_to), 30);

                    dispatch({
                      type: 'UPDATE_DATE',
                      payload: {
                        new_date_to,
                        new_date_from,
                      },
                    });
                  },
                },
              }}
            />

            <div key="call-to-action-df" className={classnames(classes.gridRowItem, classes.callToActionGroup)}>
              <TextButton
                className={classes.clearAllAction}
                onClick={event => {
                  dispatch({ type: 'RESET', payload: { state: defaultState } });
                  // eslint-disable-next-line no-unused-expressions
                  onClear && onClear();
                  tracking.trackEvent(trackClearForm());
                  event.preventDefault();
                }}
              >
                <ReplayIcon></ReplayIcon>
                {i18NText.clearAll}
              </TextButton>
              <Button
                type="submit"
                onClick={event => {
                  if (typeof onSubmit === 'function') {
                    const searchQuery = getFinalQueryConditions(filterState);
                    onSubmit(searchQuery);
                    tracking.trackEvent(trackSearchGo(searchQuery));
                  }

                  if (viewMode === filterViewMode.MOBILE) {
                    setDrawerOpen(false);
                  }

                  event.preventDefault();
                }}
                className={classnames(classes.searchAction)}
              >
                {i18NText.search}
              </Button>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
  const mobileSearchSection = (
    <section className={classnames(classes.gridContainer, classes.mobileSearch_container)}>
      <div className={classnames(classes.gridRow, classes.mobileSearch_row)}>
        <div className={classnames(classes.titleSection)}>
          <div className={classes.titleGroup}>
            <h3 className={classes.title}>{i18NText.pageTitle}</h3>
          </div>
          <div className={classnames(classes.mobileSearch_search)}>
            <IconButton
              className={classes.mobileSearch_icon}
              color="inherit"
              aria-label="Search"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <SearchIcon className={classes.mobileSearch_icon_absolute} />
            </IconButton>
          </div>
        </div>
      </div>
    </section>
  );
  const mobileFilterForm = (
    <BottomSheet open={isDrawerOpen} actionComponent={mobileSearchSection}>
      {filterForm}
    </BottomSheet>
  );
  return viewMode !== filterViewMode.MOBILE ? filterForm : mobileFilterForm;
}
